import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { ObjectInterface } from '@interfaces';

import { PUBLIC_URL } from '@constants';
import { matchPermission } from '@helpers/permission';

import { ActionType } from '@store/interfaces/auth';

import { Icon } from '@components/base';
import { useAppDispatch, useAppSelector } from '@hooks';

import { STONE_NAV_ITEMS } from './navItems';

const StoneHeader = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth);
  const permissions = useAppSelector((state) => (state.auth?.user?.permissions || {}) as ObjectInterface);
  const handleLogout = () => dispatch({ type: ActionType.LOGOUT });

  const currentLang = i18n.language || 'en';

  const toggleLanguage = () => {
    const nextLang = currentLang.startsWith('zh') ? 'en' : 'zh';
    i18n.changeLanguage(nextLang);
  };

  const navItems = useMemo(() => STONE_NAV_ITEMS.filter((item) => !item.permission || matchPermission(item.permission, permissions)), [permissions]);

  return (
    <header className="bg-white border-b border-gray-100 px-8 py-4 flex items-center justify-between sticky top-0 z-50 shadow-sm">
      <div className="flex items-center gap-12">
        <Link to="/" className="flex items-center">
          <img src={`${PUBLIC_URL}/logo.svg`} alt="Glamira" className="h-8 dark:invert" />
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`text-sm font-bold tracking-wide transition-all duration-200 relative py-2 ${
                location.pathname === item.to
                  ? 'text-blue-600 after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-blue-600'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              {t(item.label)}
            </Link>
          ))}
        </nav>
      </div>

      <div className="flex items-center gap-6">
        <div
          onClick={toggleLanguage}
          className="hidden lg:flex items-center gap-2 text-sm font-bold text-gray-500 cursor-pointer hover:text-gray-900 transition-colors bg-gray-50 px-3 py-1.5 rounded-lg"
        >
          <span>{currentLang.startsWith('zh') ? 'EN' : '中文'}</span>
          <Icon name="arrow-down" className="w-3 h-3" />
        </div>

        <div className="flex items-center gap-3 pl-6 border-l border-gray-100">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-bold text-gray-900">{user?.user?.name || 'Ümit'}</p>
          </div>
          <div className="dropdown dropdown-end">
            <label tabIndex={0} className="cursor-pointer block">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold shadow-lg shadow-blue-100 overflow-hidden border-2 border-white">
                <img src={`${PUBLIC_URL}/static/assets/images/avatar-layouts-5.png`} alt="avatar" className="w-full h-full object-cover" />
              </div>
            </label>
            <ul tabIndex={0} className="dropdown-content mt-4 p-2 shadow-2xl bg-white rounded-2xl border border-gray-100 w-56">
              <li>
                <Link to="/dashboard" className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors">
                  <Icon name="favorite-chart" className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-bold text-gray-700">{t('Back to Old Dashboard')}</span>
                </Link>
              </li>
              <div className="h-px bg-gray-100 my-2 mx-2" />
              <li>
                <button onClick={handleLogout} className="w-full flex items-center gap-3 p-3 hover:bg-rose-50 rounded-xl transition-colors group">
                  <Icon name="logout" className="w-5 h-5 text-gray-400 group-hover:text-rose-500" />
                  <span className="text-sm font-bold text-gray-700 group-hover:text-rose-600">{t('Log out')}</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </header>
  );
};

export default StoneHeader;
